// Rounding was necessary to prevent this: https://app.shortcut.com/audette/story/12915/round-existing-numbers-to-four-decimal-places-in-the-measure-edit-screen
import { roundTo } from "utils/formatting";

export const removeMeasureInputUid = (measures: CarbonReductionMeasureInput[]) =>
    measures.map((measure): CarbonReductionMeasureInput => measure);

export const measureToMeasureInput = (measure: CarbonReductionMeasure): CarbonReductionMeasureInput => ({
    ...measure,
    measureUid: measure.carbonReductionMeasureUid,
    year: measure.yearApplied,
    crmId: measure.carbonReductionMeasureId,
    measureCost: measure.userProvidedCost ? roundTo(measure.measureCost, 4) : null,
    measureSize: measure.userProvidedSize ? roundTo(measure.measureSize, 4) : null,
    totalIncentives: measure.userProvidedIncentives ? roundTo(measure.totalIncentives, 4) : null,
    category: measure.category,
    description: measure.userProvidedDescription ? measure.description : null,
    lifetime: roundTo(measure.measureLife, 4) || null,
    name: measure.carbonReductionMeasureType,
    sizeUnit: measure.sizeUnit,
    revenueEffect: measure.revenueEffect
        ? {
              annualRevenue: roundTo(measure.revenueEffect.revenueByYear[0], 4),
              description: measure.revenueEffect.description,
          }
        : null,
    landlordShareCost:
        measure.userProvidedLandlordShareCost && measure.landlordShareCost
            ? roundTo(measure.landlordShareCost, 4)
            : null,
    landlordShareElectricity: measure.userProvidedLandlordShareElectricity
        ? roundTo(measure.landlordShareElectricity, 4)
        : null,
    landlordShareNaturalGas: measure.userProvidedLandlordShareNaturalGas
        ? roundTo(measure.landlordShareNaturalGas, 4)
        : null,
    landlordShareSteam: measure.userProvidedLandlordShareSteam ? roundTo(measure.landlordShareSteam, 4) : null,
    likeForLikeCost: measure.userProvidedLikeForLikeCost ? roundTo(measure.likeForLikeCost, 4) : null,
    loadReductionEffects: measure.loadReductionEffects.map((effect) => ({
        ...effect,
        uid: crypto.randomUUID(),
    })),
    consumptionReductionEffects: measure.consumptionReductionEffects.map((effect) => ({
        ...effect,
        uid: crypto.randomUUID(),
    })),
    fuelCostReductionEffects: measure.fuelCostReductionEffects.map((effect) => ({
        ...effect,
        uid: crypto.randomUUID(),
    })),
    carbonIntensityReductionEffects: measure.carbonIntensityReductionEffects.map((effect) => ({
        ...effect,
        uid: crypto.randomUUID(),
    })),
    notes: measure.notes,
    implementationStatus: measure.implementationStatus,
});

export const measuresToMeasureInput = (measures: CarbonReductionMeasure[]) =>
    measures
        .filter(
            (measure): measure is NonNullable<typeof measure> =>
                measure !== undefined && measure.carbonReductionMeasureId !== "no-action"
        )
        .map((measure: CarbonReductionMeasure): CarbonReductionMeasureInput => measureToMeasureInput(measure));

export const addProjectToMeasures = (
    measures: CarbonReductionMeasureInput[],
    newMeasure: CarbonReductionMeasureInput
) => [...measures, newMeasure];

export const removeProjectsFromMeasures = (measures: CarbonReductionMeasureInput[], measureUids: string[]) =>
    measures.filter((measure) => !measureUids.includes(measure.measureUid || ""));

export const editProjectInMeasures = (
    measures: CarbonReductionMeasureInput[],
    selectedMeasureUid: string,
    updatedMeasure: CarbonReductionMeasureInput
): CarbonReductionMeasureInput[] =>
    measures.map((measure) => (measure.measureUid === selectedMeasureUid ? updatedMeasure : measure));

export const getSelectedMeasuresUids = (selectedRows: CustomPlanDataSource[]) =>
    selectedRows.map((selectedRow) => selectedRow.carbonReductionMeasureUid);

export const getSelectedMeasureUid = (selectedRows: CustomPlanDataSource[]) => {
    const uuids = getSelectedMeasuresUids(selectedRows);
    if (uuids.length !== 1) {
        console.error("More than one row selected.");
    }
    return uuids[0];
};

export const getSelectedPlan = (buildingModel: Building, carbonReductionPlanUid: string) => {
    const targetPlanIndex = buildingModel?.carbonReductionPlans.findIndex(
        (plan) => plan?.id === carbonReductionPlanUid
    );

    if (targetPlanIndex === undefined || targetPlanIndex === -1) {
        console.error("No matching carbon reduction plan found");
        return null;
    }

    return buildingModel?.carbonReductionPlans[targetPlanIndex];
};

export const currentYear = new Date().getFullYear();

/**
 * Measure scenario objects from `AgentResultArtifact["data"]["scenarios"]` when `agent === "measure_design"`
 * (see queryBuildingChatHistory + measure_design_bot). Keep in sync with measure_design_bot schema
 * (build_format_instructions / _scenarios_json_example).
 */
interface MeasureDesignScenario {
    crmId?: string;
    year?: number;
    name?: string | null;
    description?: string | null;
    measureCost?: number | null;
    measureSize?: number | null;
    totalIncentives?: number | null;
    sizeUnit?: string | null;
    lifetime?: number | null;
    category?: string | null;
    notes?: string | null;
    implementationStatus?: string | null;
    loadReductionEffects?: Array<{ endUse?: string; percentReduction?: number | null }>;
    consumptionReductionEffects?: Array<{ endUse?: string; fuel?: string; percentReduction?: number | null }>;
    fuelCostReductionEffects?: Array<{
        fuel?: string;
        percentReduction?: number | null;
        absoluteReduction?: number | null;
    }>;
    carbonIntensityReductionEffects?: Array<{ fuel?: string; percentReduction?: number | null }>;
    revenueEffect?: { annualRevenue?: number | null; description?: string | null } | null;
}

function withUid<T extends Record<string, unknown>>(obj: T): T & { uid: string } {
    return { ...obj, uid: crypto.randomUUID() };
}

export function measureDesignScenarioToMeasureInput(scenario: MeasureDesignScenario): CarbonReductionMeasureInput {
    const name = (scenario.name ?? "").trim() || null;

    return {
        measureUid: undefined,
        crmId: scenario.crmId ?? "generic",
        year: scenario.year ?? null,
        measureCost: scenario.measureCost ?? null,
        measureSize: scenario.measureSize ?? null,
        totalIncentives: scenario.totalIncentives ?? null,
        category: scenario.category ?? null,
        description: scenario.description ?? null,
        lifetime: scenario.lifetime ?? null,
        name,
        sizeUnit: scenario.sizeUnit ?? null,
        landlordShareCost: null,
        landlordShareElectricity: null,
        landlordShareNaturalGas: null,
        landlordShareSteam: null,
        likeForLikeCost: null,
        loadReductionEffects: (scenario.loadReductionEffects ?? []).map((e) =>
            withUid({
                endUse: e.endUse ?? "",
                percentReduction: e.percentReduction ?? null,
            })
        ),
        revenueEffect:
            scenario.revenueEffect != null
                ? {
                      annualRevenue: scenario.revenueEffect.annualRevenue ?? null,
                      description: scenario.revenueEffect.description ?? null,
                  }
                : null,
        consumptionReductionEffects: (scenario.consumptionReductionEffects ?? []).map((e) =>
            withUid({
                endUse: e.endUse ?? null,
                fuel: e.fuel ?? null,
                percentReduction: e.percentReduction ?? null,
            })
        ),
        fuelCostReductionEffects: (scenario.fuelCostReductionEffects ?? []).map((e, i) => ({
            ...withUid({
                fuel: e.fuel ?? "",
                percentReduction: e.percentReduction ?? null,
                absoluteReduction: e.absoluteReduction ?? null,
            }),
            id: i,
        })),
        carbonIntensityReductionEffects: (scenario.carbonIntensityReductionEffects ?? []).map((e, i) => ({
            ...withUid({
                fuel: e.fuel ?? null,
                percentReduction: e.percentReduction ?? null,
            }),
            id: i,
        })),
        userProvidedCost: null,
        userProvidedIncentives: null,
        userProvidedSize: null,
        userProvidedLandlordShareCost: null,
        userProvidedLandlordShareElectricity: null,
        userProvidedLandlordShareNaturalGas: null,
        userProvidedLandlordShareSteam: null,
        notes: scenario.notes ?? null,
        implementationStatus: scenario.implementationStatus ?? null,
    } as CarbonReductionMeasureInput;
}
